/**
 * QR Studio shared utilities — callable helper, public link builders,
 * client-side QR rendering (the `qrcode` lib draws to canvas/SVG; the encoded
 * content is ALWAYS the fhl.one/q/{code} tracking short link), and the
 * camelCase draft <-> snake_case row mapping for the form builder.
 */

import QRCode from 'qrcode';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../../../utils/init-firebase';

export const PUBLIC_ORIGIN = 'https://fhl.one';

const fns = () => getFunctions(app, 'us-east1');
export const call = (name, payload) => httpsCallable(fns(), name)(payload).then((r) => r.data);

export const shortLinkUrl = (shortCode) => `${PUBLIC_ORIGIN}/q/${shortCode}`;
export const formPublicUrl = (slug) => `${PUBLIC_ORIGIN}/f/${slug}`;
export const bookingPublicUrl = (page) => `${PUBLIC_ORIGIN}/book/${page.owner_handle}/${page.slug}`;

export function slugifyName(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// ---------------------------------------------------------------------------
// QR rendering (error correction H so a center logo stays scannable)
// ---------------------------------------------------------------------------

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Logo image failed to load'));
    img.src = url;
  });
}

function drawRoundedRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Render the QR (plus optional center logo) onto a fresh canvas. */
export async function renderQrCanvas(text, design = {}, size = 512) {
  const canvas = document.createElement('canvas');
  await QRCode.toCanvas(canvas, text, {
    width: size,
    margin: 2,
    errorCorrectionLevel: 'H',
    color: {
      dark: design.darkColor || '#1A365D',
      light: design.lightColor || '#FFFFFF',
    },
  });

  if (design.logoUrl) {
    try {
      const img = await loadImage(design.logoUrl);
      const ctx = canvas.getContext('2d');
      const logoSize = Math.round(size * 0.2);
      const pad = Math.round(logoSize * 0.18);
      const bx = (size - logoSize) / 2 - pad;
      const bSize = logoSize + pad * 2;
      ctx.fillStyle = design.lightColor || '#FFFFFF';
      drawRoundedRect(ctx, bx, bx, bSize, bSize, Math.round(bSize * 0.18));
      ctx.fill();
      ctx.drawImage(img, (size - logoSize) / 2, (size - logoSize) / 2, logoSize, logoSize);
    } catch (err) {
      // Logo failing to load (CORS, dead URL) must never block the QR itself.
      console.warn('[qrStudio] logo overlay skipped:', err.message);
    }
  }

  return canvas;
}

/** Data URL for on-screen previews. */
export async function qrDataUrl(text, design = {}, size = 320) {
  const canvas = await renderQrCanvas(text, design, size);
  return canvas.toDataURL('image/png');
}

/** SVG markup (vector, for designers/printers). Logo embedded as <image>. */
export async function qrSvgString(text, design = {}) {
  let svg = await QRCode.toString(text, {
    type: 'svg',
    margin: 2,
    errorCorrectionLevel: 'H',
    color: {
      dark: design.darkColor || '#1A365D',
      light: design.lightColor || '#FFFFFF',
    },
  });

  if (design.logoUrl) {
    const match = svg.match(/viewBox="0 0 (\d+(?:\.\d+)?) (\d+(?:\.\d+)?)"/);
    const units = match ? parseFloat(match[1]) : 0;
    if (units > 0) {
      const logoSize = units * 0.2;
      const pad = logoSize * 0.18;
      const bx = (units - logoSize) / 2 - pad;
      const bSize = logoSize + pad * 2;
      const overlay =
        `<rect x="${bx}" y="${bx}" width="${bSize}" height="${bSize}" rx="${bSize * 0.18}" ` +
        `fill="${design.lightColor || '#FFFFFF'}"/>` +
        `<image href="${design.logoUrl}" x="${(units - logoSize) / 2}" y="${(units - logoSize) / 2}" ` +
        `width="${logoSize}" height="${logoSize}"/>`;
      svg = svg.replace('</svg>', `${overlay}</svg>`);
    }
  }

  return svg;
}

function triggerDownload(href, filename) {
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/** Print-resolution PNG download (1024px with quiet zone). */
export async function downloadQrPng(qr, filenameBase) {
  const canvas = await renderQrCanvas(shortLinkUrl(qr.short_code), qr.design || {}, 1024);
  triggerDownload(canvas.toDataURL('image/png'), `${filenameBase || 'qr-code'}.png`);
}

/** Vector SVG download. */
export async function downloadQrSvg(qr, filenameBase) {
  const svg = await qrSvgString(shortLinkUrl(qr.short_code), qr.design || {});
  const blob = new Blob([svg], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  triggerDownload(url, `${filenameBase || 'qr-code'}.svg`);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

// ---------------------------------------------------------------------------
// Form draft mapping (builder works in camelCase; DB rows are snake_case)
// ---------------------------------------------------------------------------

export const DEFAULT_CONSENT_TEXT =
  'By submitting, you agree that Future Home Loans may contact you about your inquiry by phone, text, or email. Message and data rates may apply.';

export function newFormDraft(targetUser) {
  return {
    id: null,
    title: '',
    slug: '',
    isActive: true,
    headline: "Let's connect",
    introText: 'Fill out your information and we\u2019ll be in touch shortly.',
    accentColor: '#1A365D',
    logoUrl: '',
    loName: targetUser?.name || '',
    loTitle: 'Loan Officer',
    loNmls: '',
    buttonText: 'Submit',
    consentText: DEFAULT_CONSENT_TEXT,
    fields: [
      // First/Last are separate on purpose — Origin can't reliably split a
      // single "full name" box (multi-word last names, suffixes, etc.)
      { id: `fld-${Date.now()}-1`, type: 'text', label: 'First Name', placeholder: 'Jane', required: true, options: [], mapTo: 'firstName' },
      { id: `fld-${Date.now()}-2`, type: 'text', label: 'Last Name', placeholder: 'Smith', required: true, options: [], mapTo: 'lastName' },
      { id: `fld-${Date.now()}-3`, type: 'email', label: 'Email', placeholder: 'jane@email.com', required: true, options: [], mapTo: 'email' },
      { id: `fld-${Date.now()}-4`, type: 'phone', label: 'Phone', placeholder: '(555) 555-5555', required: false, options: [], mapTo: 'phone' },
    ],
    postSubmitType: 'message',
    postSubmitMessage: 'Thanks! Your information has been received \u2014 we\u2019ll reach out shortly.',
    postSubmitRedirectUrl: '',
    postSubmitBookingPageId: null,
    crmCreateDeal: false,
    crmTags: [],
    crmSource: '',
    notifyOnSubmission: true,
  };
}

export function rowToDraft(row) {
  return {
    id: row.id,
    title: row.title || '',
    slug: row.slug || '',
    isActive: row.is_active !== false,
    headline: row.headline || '',
    introText: row.intro_text || '',
    accentColor: row.accent_color || '#1A365D',
    logoUrl: row.logo_url || '',
    loName: row.lo_name || '',
    loTitle: row.lo_title || '',
    loNmls: row.lo_nmls || '',
    buttonText: row.button_text || 'Submit',
    consentText: row.consent_text || '',
    fields: Array.isArray(row.fields) ? row.fields.map((f) => ({ options: [], ...f })) : [],
    postSubmitType: row.post_submit_type || 'message',
    postSubmitMessage: row.post_submit_message || '',
    postSubmitRedirectUrl: row.post_submit_redirect_url || '',
    postSubmitBookingPageId: row.post_submit_booking_page_id || null,
    crmCreateDeal: row.crm_create_deal === true,
    crmTags: Array.isArray(row.crm_tags) ? row.crm_tags : [],
    crmSource: row.crm_source || '',
    notifyOnSubmission: row.notify_on_submission !== false,
  };
}

/** Shape the draft like a crm_lead_forms row so LeadFormRenderer can preview it. */
export function draftToPreviewRow(draft) {
  return {
    title: draft.title,
    headline: draft.headline,
    intro_text: draft.introText,
    accent_color: draft.accentColor,
    logo_url: draft.logoUrl,
    lo_name: draft.loName,
    lo_title: draft.loTitle,
    lo_nmls: draft.loNmls,
    button_text: draft.buttonText,
    consent_text: draft.consentText,
    fields: draft.fields,
  };
}
